import React, { useRef, useState } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Eraser,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  PaintBucket,
  Palette,
  Quote,
  Redo2,
  Underline,
  Undo2,
} from 'lucide-react';

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'UL', 'OL', 'FIGURE']);
const SAVED_BLOCK_REGEX = /^\s*<(p|h2|h3|blockquote|ul|ol|figure)\b/i;
const FONT_SIZE_MAP = {
  1: '12px',
  2: '14px',
  3: '16px',
  4: '18px',
  5: '24px',
  6: '32px',
  7: '40px',
};

const escapeHtml = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const isSafeUrl = (value = '') => /^(https?:\/\/|mailto:)/i.test(value);
const isSafeImageSrc = (value = '') => /^(https?:\/\/|data:image\/(png|jpe?g|gif|webp);base64,)/i.test(value);

const sanitizeCssValue = (value = '') => {
  const trimmed = value.trim();
  return /^[#(),.%\w\s-]+$/.test(trimmed) ? trimmed : '';
};

const getStyleAttribute = (node, properties = ['color', 'backgroundColor', 'fontSize']) => {
  const declarations = [];

  properties.forEach((property) => {
    const value = sanitizeCssValue(node.style?.[property] || '');
    if (!value) return;

    const cssName = property.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
    declarations.push(`${cssName}: ${value}`);
  });

  if (node.tagName === 'FONT') {
    const fontSize = FONT_SIZE_MAP[node.getAttribute('size')];
    const color = sanitizeCssValue(node.getAttribute('color') || '');
    if (fontSize) declarations.push(`font-size: ${fontSize}`);
    if (color) declarations.push(`color: ${color}`);
  }

  return declarations.length ? ` style="${escapeHtml(declarations.join('; '))}"` : '';
};

const wrapStyledInline = (node, children) => {
  const styleAttribute = getStyleAttribute(node);
  return styleAttribute ? `<span${styleAttribute}>${children}</span>` : children;
};

const getBlockAttributes = (node) => {
  const textAlign = sanitizeCssValue(node.style?.textAlign || node.getAttribute('align') || '');
  return ['left', 'center', 'right', 'justify'].includes(textAlign)
    ? ` style="text-align: ${textAlign}"`
    : '';
};

const contentToEditorHtml = (content = []) => {
  const paragraphs = content.filter(paragraph => paragraph?.trim());
  if (!paragraphs.length) return '<p><br></p>';
  return paragraphs
    .map(paragraph => SAVED_BLOCK_REGEX.test(paragraph) ? paragraph : `<p>${paragraph}</p>`)
    .join('');
};

const serializeInlineContent = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent.replace(/\u00a0/g, ' '));
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const children = Array.from(node.childNodes).map(serializeInlineContent).join('');
  const tagName = node.tagName;

  if (tagName === 'BR') return '\n';
  if (tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    const alt = node.getAttribute('alt') || '';
    return isSafeImageSrc(src)
      ? `<figure><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" /></figure>`
      : '';
  }
  if (tagName === 'B' || tagName === 'STRONG') return `<b>${wrapStyledInline(node, children)}</b>`;
  if (tagName === 'I' || tagName === 'EM') return `<i>${wrapStyledInline(node, children)}</i>`;
  if (tagName === 'U') return `<u>${wrapStyledInline(node, children)}</u>`;
  if (tagName === 'A') {
    const href = node.getAttribute('href') || '';
    return isSafeUrl(href)
      ? `<a href="${escapeHtml(href)}">${wrapStyledInline(node, children)}</a>`
      : children;
  }
  if (tagName === 'SPAN' || tagName === 'FONT') return wrapStyledInline(node, children);

  return children;
};

const serializeBlockContent = (node) => {
  const children = Array.from(node.childNodes).map(serializeInlineContent).join('').trim();
  const tagName = node.tagName;

  if (!children && tagName !== 'UL' && tagName !== 'OL') return '';
  if (tagName === 'FIGURE') return children;
  if (tagName === 'H2') return `<h2${getBlockAttributes(node)}>${children}</h2>`;
  if (tagName === 'H3') return `<h3${getBlockAttributes(node)}>${children}</h3>`;
  if (tagName === 'BLOCKQUOTE') return `<blockquote${getBlockAttributes(node)}>${children}</blockquote>`;
  if (tagName === 'UL' || tagName === 'OL') {
    const items = Array.from(node.children)
      .filter(child => child.tagName === 'LI')
      .map(child => `<li>${Array.from(child.childNodes).map(serializeInlineContent).join('').trim()}</li>`)
      .filter(item => item !== '<li></li>')
      .join('');

    return items ? `<${tagName.toLowerCase()}${getBlockAttributes(node)}>${items}</${tagName.toLowerCase()}>` : '';
  }

  return `<p${getBlockAttributes(node)}>${children}</p>`;
};

export const htmlToRichContent = (html = '') => {
  const container = document.createElement('div');
  container.innerHTML = html;
  const paragraphs = [];
  let inlineBuffer = '';

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName)) {
      if (inlineBuffer.trim()) {
        paragraphs.push(inlineBuffer.trim());
        inlineBuffer = '';
      }

      const paragraph = serializeBlockContent(node);
      if (paragraph) paragraphs.push(paragraph);
      return;
    }

    inlineBuffer += serializeInlineContent(node);
  });

  if (inlineBuffer.trim()) paragraphs.push(inlineBuffer.trim());

  return paragraphs.map(paragraph => paragraph.trim()).filter(Boolean);
};

const RichTextEditor = ({ value = [''], onChange, onError, minHeight = 360 }) => {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [editorHtml] = useState(contentToEditorHtml(value));

  const syncEditorContent = () => {
    const nextContent = htmlToRichContent(editorRef.current?.innerHTML || '');
    onChange(nextContent.length ? nextContent : ['']);
  };

  const runCommand = (command, argument = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, argument);
    syncEditorContent();
  };

  const applyLink = () => {
    const url = window.prompt('Enter link URL');
    if (!url) return;
    const href = /^(https?:\/\/|mailto:)/i.test(url) ? url : `https://${url}`;
    runCommand('createLink', href);
  };

  const removeFormatting = () => {
    runCommand('removeFormat');
    runCommand('unlink');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const pastedContent = html
      ? htmlToRichContent(html)
      : text.split(/\n+/).map(line => line.trim()).filter(Boolean);

    document.execCommand('insertHTML', false, contentToEditorHtml(pastedContent));
    syncEditorContent();
  };

  const insertImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      onError?.('Only image files can be inserted into content');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      onError?.('Inline content image size should be less than 2MB');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      editorRef.current?.focus();
      const altText = escapeHtml(file.name.replace(/\.[^/.]+$/, ''));
      document.execCommand(
        'insertHTML',
        false,
        `<figure><img src="${reader.result}" alt="${altText}" /></figure><p><br></p>`
      );
      syncEditorContent();
      e.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-sky-500 focus-within:border-sky-500">
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-300">
        <button type="button" onClick={() => runCommand('formatBlock', 'p')} className="px-3 py-2 text-sm hover:bg-gray-200 rounded" title="Paragraph">P</button>
        <button type="button" onClick={() => runCommand('formatBlock', 'h2')} className="p-2 hover:bg-gray-200 rounded" title="Heading"><Heading2 size={16} /></button>
        <button type="button" onClick={() => runCommand('formatBlock', 'h3')} className="p-2 hover:bg-gray-200 rounded" title="Subheading"><Heading3 size={16} /></button>
        <span className="mx-1 h-6 w-px bg-gray-300" />
        <button type="button" onClick={() => runCommand('bold')} className="p-2 hover:bg-gray-200 rounded" title="Bold"><Bold size={16} /></button>
        <button type="button" onClick={() => runCommand('italic')} className="p-2 hover:bg-gray-200 rounded" title="Italic"><Italic size={16} /></button>
        <button type="button" onClick={() => runCommand('underline')} className="p-2 hover:bg-gray-200 rounded" title="Underline"><Underline size={16} /></button>
        <button type="button" onClick={applyLink} className="p-2 hover:bg-gray-200 rounded" title="Link"><LinkIcon size={16} /></button>
        <span className="mx-1 h-6 w-px bg-gray-300" />
        <select defaultValue="3" onChange={(e) => runCommand('fontSize', e.target.value)} className="h-9 rounded border border-gray-300 bg-white px-2 text-sm" title="Font size">
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="4">Medium</option>
          <option value="5">Large</option>
          <option value="6">Huge</option>
        </select>
        <label className="flex h-9 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-sm cursor-pointer hover:bg-gray-100" title="Text color">
          <Palette size={16} />
          <input type="color" className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0" onChange={(e) => runCommand('foreColor', e.target.value)} />
        </label>
        <label className="flex h-9 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-sm cursor-pointer hover:bg-gray-100" title="Background color">
          <PaintBucket size={16} />
          <input type="color" className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0" onChange={(e) => runCommand('hiliteColor', e.target.value)} />
        </label>
        <span className="mx-1 h-6 w-px bg-gray-300" />
        <button type="button" onClick={() => runCommand('justifyLeft')} className="p-2 hover:bg-gray-200 rounded" title="Align left"><AlignLeft size={16} /></button>
        <button type="button" onClick={() => runCommand('justifyCenter')} className="p-2 hover:bg-gray-200 rounded" title="Align center"><AlignCenter size={16} /></button>
        <button type="button" onClick={() => runCommand('justifyRight')} className="p-2 hover:bg-gray-200 rounded" title="Align right"><AlignRight size={16} /></button>
        <button type="button" onClick={() => runCommand('justifyFull')} className="p-2 hover:bg-gray-200 rounded" title="Justify"><AlignJustify size={16} /></button>
        <span className="mx-1 h-6 w-px bg-gray-300" />
        <button type="button" onClick={() => runCommand('insertUnorderedList')} className="p-2 hover:bg-gray-200 rounded" title="Bulleted list"><List size={16} /></button>
        <button type="button" onClick={() => runCommand('insertOrderedList')} className="p-2 hover:bg-gray-200 rounded" title="Numbered list"><ListOrdered size={16} /></button>
        <button type="button" onClick={() => runCommand('formatBlock', 'blockquote')} className="p-2 hover:bg-gray-200 rounded" title="Quote"><Quote size={16} /></button>
        <button type="button" onClick={() => imageInputRef.current?.click()} className="p-2 hover:bg-gray-200 rounded" title="Insert image inside content"><ImagePlus size={16} /></button>
        <input ref={imageInputRef} type="file" className="hidden" accept="image/*" onChange={insertImage} />
        <span className="mx-1 h-6 w-px bg-gray-300" />
        <button type="button" onClick={() => runCommand('undo')} className="p-2 hover:bg-gray-200 rounded" title="Undo"><Undo2 size={16} /></button>
        <button type="button" onClick={() => runCommand('redo')} className="p-2 hover:bg-gray-200 rounded" title="Redo"><Redo2 size={16} /></button>
        <button type="button" onClick={removeFormatting} className="p-2 hover:bg-gray-200 rounded" title="Clear formatting"><Eraser size={16} /></button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label="Content editor"
        onInput={syncEditorContent}
        onPaste={handlePaste}
        className="w-full p-4 text-gray-800 leading-relaxed outline-none overflow-y-auto [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-sky-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_figure]:my-4 [&_figure_img]:max-h-96 [&_figure_img]:max-w-full [&_figure_img]:rounded-lg [&_figure_img]:object-contain"
        style={{ minHeight }}
        dangerouslySetInnerHTML={{ __html: editorHtml }}
      />
    </div>
  );
};

export default RichTextEditor;
